import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { User, Mail, Phone, Shield, Calendar, Users, CheckCircle, Clock, XCircle, Building2, Headphones } from "lucide-react";
import { api, getStoredUser } from "@/lib/api";

export default function AgentProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const stored = getStoredUser();
  const site = useOutletContext();

  useEffect(() => {
    api.get("/agent/profile")
      .then(r => setProfile(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="pt-10 flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-brand-primary/30 border-t-brand-primary rounded-full animate-spin" />
        <p className="text-sm text-slate-400">Loading profile…</p>
      </div>
    );
  }

  const user = profile?.user || stored || {};
  const agent = profile?.agent || {};
  const isActive = agent.status === "active";
  const memberSince = profile?.member_since
    ? new Date(profile.member_since).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })
    : "—";

  return (
    <div className="space-y-6 pb-8">
      <h2 className="font-heading font-bold text-2xl text-brand-nav">My Profile</h2>

      {/* ── Hero Card ───────────────────────────────────────── */}
      <div className="relative overflow-hidden bg-gradient-to-br from-brand-primary via-brand-primary/90 to-brand-accent rounded-2xl p-6 text-white">
        {/* Decorative circles */}
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/5 rounded-full" />
        <div className="absolute -bottom-6 -left-6 w-28 h-28 bg-white/5 rounded-full" />

        <div className="relative flex flex-col sm:flex-row items-center sm:items-start gap-5">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-3xl font-bold shadow-lg ring-2 ring-white/30">
            {(user.name || "?")[0].toUpperCase()}
          </div>

          <div className="flex-1 text-center sm:text-left">
            <h3 className="font-heading font-bold text-2xl">{user.name || "Agent"}</h3>
            <div className="flex flex-wrap justify-center sm:justify-start items-center gap-2 mt-2">
              <span className="inline-flex items-center gap-1 text-xs font-semibold bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full uppercase tracking-wider">
                <User className="w-3 h-3" />
                {user.role || "agent"}
              </span>
              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider ${isActive ? "bg-emerald-400/25 text-emerald-100" : "bg-red-400/25 text-red-100"}`}>
                <Shield className="w-3 h-3" />
                {agent.status || "—"}
              </span>
            </div>
            {profile?.admin_brand && (
              <p className="mt-2 text-sm opacity-80 flex items-center justify-center sm:justify-start gap-1.5">
                <Building2 className="w-4 h-4 opacity-60" />
                {profile.admin_brand}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Stats Row ───────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: Users,       label: "Total Leads", value: profile?.total_requests ?? 0,    color: "blue"   },
          { icon: Clock,       label: "Pending",     value: profile?.pending_requests ?? 0,  color: "amber"  },
          { icon: CheckCircle, label: "Approved",    value: profile?.approved_requests ?? 0, color: "emerald"},
          { icon: XCircle,     label: "Rejected",    value: profile?.rejected_requests ?? 0, color: "red"    },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col items-center text-center hover:shadow-md transition-shadow">
            <div className={`w-10 h-10 rounded-lg bg-${color}-50 flex items-center justify-center mb-2`}>
              <Icon className={`w-5 h-5 text-${color}-500`} />
            </div>
            <p className="font-heading font-bold text-xl text-brand-nav">{value}</p>
            <p className="text-xs text-slate-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* ── Contact Details ──────────────────────────────────── */}
      <div>
        <h3 className="font-heading font-semibold text-sm text-slate-400 uppercase tracking-wider mb-3">Contact Details</h3>
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-blue-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-slate-400">Email</p>
              <p className="text-sm font-semibold text-slate-700 truncate">{user.email || "Not set"}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
              <Phone className="w-5 h-5 text-green-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-slate-400">Mobile</p>
              <p className="text-sm font-semibold text-slate-700">{user.mobile || "—"}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-violet-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-slate-400">Member Since</p>
              <p className="text-sm font-semibold text-slate-700">{memberSince}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-purple-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-slate-400">Account Status</p>
              <p className={`text-sm font-semibold ${isActive ? "text-green-600" : "text-red-500"}`}>
                {isActive ? "Active" : agent.status || "—"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Help & Support ───────────────────────────────────── */}
      <div className="pt-6">
        <h3 className="font-heading font-semibold text-sm text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Headphones className="w-4 h-4" /> Help & Support
        </h3>
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4 shadow-sm">
          <p className="text-sm text-slate-500 border-b border-slate-100 pb-3">If you need assistance, please contact your admin via the details below.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Email</p>
              <p className="text-sm font-medium text-slate-800 mt-1 break-all">{site?.email || "Not provided"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Phone</p>
              <p className="text-sm font-medium text-slate-800 mt-1">{site?.phone || "Not provided"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">WhatsApp</p>
              <p className="text-sm font-medium text-slate-800 mt-1">{site?.whatsapp || "Not provided"}</p>
            </div>
          </div>
        </div>
      </div>

      <p className="text-xs text-center text-slate-400 pt-2">
        To update your profile details, please contact your admin.
      </p>
    </div>
  );
}
